import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Select,
  FormControl, InputLabel, Chip, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, IconButton,
  Tooltip, Alert, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Drawer, Divider, List, ListItem, ListItemText,
  Tabs, Tab, Switch, FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  OpenInNew as OpenIcon,
  LocalShipping as SupplierIcon,
  StoreMallDirectory as CustomerIcon,
  Warehouse as WarehouseIcon,
  LocalOffer as TagIcon,
  Add as AddIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  CloudUpload as ImportIcon,
  CloudDownload as ExportIcon,
  EditNote as BulkEditIcon,
} from '@mui/icons-material';
import { PageHeader } from '../ds';
import api from '../../config/api';
import { formatCountry } from '../../utils/countryFormat';
import EntityActionsMenu from '../common/EntityActionsMenu';
import BulkImportDialog from '../common/BulkImportDialog';
import {
  bulkImportSuppliers,
  bulkUpdateSuppliers,
  exportSuppliersCsv,
} from '../../services/procurementService';
import {
  bulkImportWholesaleCustomers,
  bulkUpdateWholesaleCustomers,
  exportWholesaleCustomersCsv,
} from '../../services/retailOrdersService';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

// from=contacts deep-link param lets the destination page render its Back
// button as "Back to Contacts" and lets the inline-edit shortcut on the
// contacts row auto-open the edit dialog (edit=1).
const TYPE_META = {
  supplier:  {
    label: 'Supplier',  color: 'primary', icon: <SupplierIcon fontSize="small" />,
    route:    id => `/procurement/suppliers/${id}?from=contacts`,
    editRoute: id => `/procurement/suppliers/${id}?from=contacts&edit=1`,
    newRoute: () => `/procurement/suppliers?new=1`,
    blurb: 'Vendor / manufacturer you buy from. Lives under Procurement → Suppliers.',
  },
  customer:  {
    label: 'Customer',  color: 'success', icon: <CustomerIcon fontSize="small" />,
    route:    id => `/retail/customers?id=${id}&from=contacts`,
    editRoute: id => `/retail/customers?id=${id}&from=contacts&edit=1`,
    newRoute: () => `/retail/customers?new=1`,
    blurb: 'Wholesale buyer (retailer / distributor). Lives under Retail → Customers.',
  },
  warehouse: {
    label: 'Warehouse', color: 'info',    icon: <WarehouseIcon fontSize="small" />,
    route:    id => `/inventory/warehouses/${id}?from=contacts`,
    editRoute: id => `/inventory/warehouses/${id}?from=contacts&edit=1`,
    newRoute: () => `/inventory/warehouses?new=1`,
    blurb: 'Physical receiving / dispatch location. Lives under Inventory → Warehouses.',
  },
};

function debounce(fn, ms) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

// Persisted view state — survives navigating to an edit screen and back, so the
// operator keeps their filters / pagination (Reported by Rishabh: editing a
// customer reset the Contacts view to default).
const CONTACTS_VIEW_KEY = 'contacts.viewState.v1';
function readContactsView() {
  try { return JSON.parse(sessionStorage.getItem(CONTACTS_VIEW_KEY)) || {}; } catch { return {}; }
}

export default function Contacts() {
  const navigate = useNavigate();
  const savedView = useMemo(readContactsView, []);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ supplier: 0, customer: 0, warehouse: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState(savedView.search ?? '');
  const [typeFilter, setTypeFilter] = useState(savedView.typeFilter ?? ''); // '' = all
  const [countryFilter, setCountryFilter] = useState(savedView.countryFilter ?? '');
  const [countries, setCountries] = useState([]);
  // Brand filter — brand is DERIVED from activity (POs / inventory / wholesale
  // orders), not stored on the contact. brandFilter holds the brand id as a string.
  const [brandFilter, setBrandFilter] = useState(savedView.brandFilter ?? '');
  const [brands, setBrands] = useState([]); // [{ id, name }]
  // include_inactive toggle. Default off — Contacts otherwise hides all
  // deactivated suppliers/customers/warehouses, with no UI path to recover
  // them. Reported by Rishabh 2026-05-21.
  const [showInactive, setShowInactive] = useState(savedView.showInactive ?? false);

  const [page, setPage] = useState(savedView.page ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(savedView.rowsPerPage ?? 25);

  // Tags
  const [tagFilter, setTagFilter] = useState(savedView.tagFilter ?? '');

  // Save the view settings whenever they change, so returning from an edit
  // screen restores filters + pagination instead of resetting to default.
  useEffect(() => {
    try {
      sessionStorage.setItem(CONTACTS_VIEW_KEY, JSON.stringify({
        search, typeFilter, countryFilter, brandFilter, showInactive, page, rowsPerPage, tagFilter,
      }));
    } catch { /* sessionStorage unavailable — non-fatal */ }
  }, [search, typeFilter, countryFilter, brandFilter, showInactive, page, rowsPerPage, tagFilter]);
  const [allTags, setAllTags] = useState([]); // [{tag, n}]
  const [tagDialogRow, setTagDialogRow] = useState(null);
  const [newTagInput, setNewTagInput] = useState('');

  // Add-contact type chooser
  const [addOpen, setAddOpen] = useState(false);

  // Bulk-actions state — Option A routing: Import/BulkUpdate dispatch based on
  // current typeFilter. importTarget tracks which entity's bulk endpoint to use.
  const [importOpen, setImportOpen] = useState(false);
  const [importMode, setImportMode] = useState('insert');
  // importTarget — 'supplier' | 'customer' (warehouse handled by deep-link).
  // Derived from typeFilter at open time so the dialog spec is stable.
  const [importTarget, setImportTarget] = useState(null);

  // Timeline drawer
  const [drawerRow, setDrawerRow] = useState(null);
  const [drawerTab, setDrawerTab] = useState(0); // 0 = overview, 1 = timeline
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [drawerOrders, setDrawerOrders] = useState([]);

  const openDrawer = (row, tab = 0) => {
    setDrawerRow(row);
    setDrawerTab(tab);
    setTimelineEvents([]);
    setDrawerOrders([]);
    if (tab === 1) loadTimeline(row);
  };

  const loadTimeline = useCallback(async (row) => {
    if (!row) return;
    setTimelineLoading(true);
    try {
      // For customers the meaningful "activity" is their order history — the
      // audit log is essentially empty for them. Load orders by qb_customer_id
      // (row.code), the same data the full Customer overview uses.
      if (row.entity_type === 'customer' && row.code) {
        const o = await api.get(`${API_BASE}/api/wholesale-orders`, { params: { customer_id: row.code } });
        setDrawerOrders(o.data?.data || []);
        setTimelineEvents([]);
      } else if (row.entity_type === 'supplier') {
        // For suppliers the meaningful "activity" is their purchase orders.
        // The endpoint returns { data: { purchase_orders, total, ... } } — unwrap
        // the array (same shape Suppliers.js handles; a non-array crashed .map).
        const p = await api.get(`${API_BASE}/api/procurement/purchase-orders`, { params: { supplier_id: row.entity_id, limit: 100 } });
        const list = Array.isArray(p.data?.data?.purchase_orders)
          ? p.data.data.purchase_orders
          : (Array.isArray(p.data?.data) ? p.data.data : (Array.isArray(p.data) ? p.data : []));
        setDrawerOrders(list);
        setTimelineEvents([]);
      } else {
        const r = await api.get(`${API_BASE}/api/contacts/${row.entity_type}/${row.entity_id}/timeline`);
        setTimelineEvents(r.data?.events || []);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setTimelineEvents([]);
      setDrawerOrders([]);
    } finally {
      setTimelineLoading(false);
    }
  }, []);

  // Cross-entity delete. Routes to the right backend endpoint by entity_type.
  // Each endpoint enforces its own reference guard (e.g. suppliers refuse if
  // any PO references them; warehouse refuses if any inventory/PO/transfer
  // references it). On 409 we surface the structured message inline.
  const [deletingRow, setDeletingRow] = useState(null);
  const [deletingInProgress, setDeletingInProgress] = useState(false);
  const [deletingError, setDeletingError] = useState(null);

  const performDelete = async () => {
    if (!deletingRow) return;
    setDeletingInProgress(true);
    setDeletingError(null);
    try {
      const t = deletingRow.entity_type;
      const id = deletingRow.entity_id;
      if (t === 'supplier') {
        await api.delete(`${API_BASE}/api/procurement/suppliers/${id}/permanent`);
      } else if (t === 'customer') {
        await api.delete(`${API_BASE}/api/wholesale-orders/customers/${id}`);
      } else if (t === 'warehouse') {
        // Warehouses use soft-delete (deactivate). Backend enforces the same
        // reference guard via POST /:id/deactivate.
        await api.post(`${API_BASE}/api/warehouses/${id}/deactivate`);
      } else {
        throw new Error(`Unknown entity_type: ${t}`);
      }
      setDeletingRow(null);
      // Reload current view. Caller's params live in component-level state; we
      // can lean on the existing useEffect re-trigger via search bump or just
      // call load() with current snapshots. Simplest: reload all with empty.
      await load(search, typeFilter, countryFilter, tagFilter, brandFilter, showInactive);
    } catch (e) {
      const d = e?.response?.data;
      const refs = Array.isArray(d?.references) ? d.references : null;
      const errMsg = d?.error || e?.message || 'Failed to delete';
      setDeletingError({ message: errMsg, references: refs });
    } finally {
      setDeletingInProgress(false);
    }
  };

  const load = useCallback(async (q, type, country, tag, brand, inactive = false) => {
    setLoading(true); setError(null);
    try {
      const params = { limit: 1000 };
      if (q) params.q = q;
      if (type) params.type = type;
      if (country) params.country = country;
      if (tag) params.tag = tag;
      if (brand) params.brand = brand;
      // Toggle ON → only inactive (filter view). Toggle OFF → only active
      // (the API's default). Earlier semantic was "include both" via
      // include_inactive=1; Rishabh reported the list was confusing because
      // turning on the toggle still showed all the active rows mixed in.
      if (inactive) params.is_active = 'false';
      const r = await api.get(`${API_BASE}/api/contacts`, { params });
      setRows(r.data?.rows || []);
      setTotals(r.data?.totals || { supplier: 0, customer: 0, warehouse: 0 });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const reloadTags = useCallback(() => {
    api.get(`${API_BASE}/api/contacts/tags`)
      .then(r => setAllTags(r.data?.tags || []))
      .catch(() => setAllTags([]));
  }, []);

  // Initial load + country list + tag list + brand list
  useEffect(() => {
    load('', '', '', '', '', showInactive);
    api.get(`${API_BASE}/api/contacts/countries`)
      .then(r => setCountries(r.data?.countries || []))
      .catch(() => setCountries([]));
    api.get(`${API_BASE}/api/contacts/brands`)
      .then(r => setBrands(r.data?.brands || []))
      .catch(() => setBrands([]));
    reloadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, reloadTags]);

  // Debounced re-fetch on filter change
  const debouncedLoad = useMemo(() => debounce((q, t, c, tg, br, inactive) => load(q, t, c, tg, br, inactive), 300), [load]);
  useEffect(() => {
    debouncedLoad(search, typeFilter, countryFilter, tagFilter, brandFilter, showInactive);
    setPage(0);
  }, [search, typeFilter, countryFilter, tagFilter, brandFilter, showInactive, debouncedLoad]);

  const handleAddTag = async () => {
    if (!tagDialogRow || !newTagInput.trim()) return;
    try {
      await api.post(
        `${API_BASE}/api/contacts/${tagDialogRow.entity_type}/${tagDialogRow.entity_id}/tags`,
        { tag: newTagInput.trim() }
      );
      setNewTagInput('');
      load(search, typeFilter, countryFilter, tagFilter, brandFilter, showInactive);
      reloadTags();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const handleRemoveTag = async (row, tag) => {
    try {
      await api.delete(
        `${API_BASE}/api/contacts/${row.entity_type}/${row.entity_id}/tags/${encodeURIComponent(tag)}`
      );
      load(search, typeFilter, countryFilter, tagFilter, brandFilter, showInactive);
      reloadTags();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const visibleRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // === Bulk-actions: Option A dispatch ==================================
  //
  // The Contacts page is a UNION view across suppliers + customers + warehouses.
  // We route Import / Update from CSV / Bulk Update through the current
  // typeFilter to the right canonical endpoint. With no filter set, those
  // actions show a friendly hint. Export is unified — client-side CSV of the
  // current filtered view across all types.

  const handleOpenImport = (mode) => {
    if (typeFilter === 'warehouse') {
      // Warehouses have their own bulk-import flow on /inventory/warehouses
      // (separate column spec + import-source handling). Deep-link rather
      // than duplicate the spec here — single source of truth wins.
      navigate('/inventory/warehouses?import=1');
      return;
    }
    if (typeFilter !== 'supplier' && typeFilter !== 'customer') {
      setError('Pick a contact type (Supplier or Customer) first — bulk actions are entity-specific.');
      return;
    }
    setImportTarget(typeFilter);
    setImportMode(mode);
    setImportOpen(true);
  };

  // Dispatch the import submit to the right backend. Single dispatch helper
  // → no per-target branches scattered through the JSX.
  const handleBulkImport = async (rowsIn) => {
    const target = importTarget;
    if (target === 'supplier') {
      if (importMode === 'update') {
        const updates = rowsIn.map(r => ({ ...r, id: parseInt(r.id, 10) }));
        const result = await bulkUpdateSuppliers(updates);
        await load(search, typeFilter, countryFilter, tagFilter, brandFilter, showInactive);
        return result;
      }
      const result = await bulkImportSuppliers(rowsIn);
      await load(search, typeFilter, countryFilter, tagFilter, brandFilter, showInactive);
      return result;
    }
    if (target === 'customer') {
      if (importMode === 'update') {
        const updates = rowsIn.map(r => ({ ...r, id: parseInt(r.id, 10) }));
        const result = await bulkUpdateWholesaleCustomers(updates);
        await load(search, typeFilter, countryFilter, tagFilter, brandFilter, showInactive);
        return result;
      }
      const result = await bulkImportWholesaleCustomers(rowsIn);
      await load(search, typeFilter, countryFilter, tagFilter, brandFilter, showInactive);
      return result;
    }
    throw new Error(`Unsupported import target: ${target}`);
  };

  // Export — type-aware. For supplier or customer typeFilters, hits the
  // canonical export endpoint (richer columns, round-trip ready). For "All"
  // or warehouse, falls back to a client-side CSV of currently visible rows
  // (more columns would need cross-DB joins; the union view doesn't have
  // address-level detail uniformly).
  const handleExport = async () => {
    try {
      if (typeFilter === 'supplier') {
        const params = {};
        if (search) params.search = search;
        await exportSuppliersCsv(params);
        return;
      }
      if (typeFilter === 'customer') {
        await exportWholesaleCustomersCsv();
        return;
      }
      // Fallback: client-side CSV from the rows currently in `rows[]`
      // (already filtered by the active filters via the /api/contacts call).
      const cols = ['entity_type', 'entity_id', 'name', 'code', 'sub_type',
        'contact_name', 'contact_email', 'contact_phone', 'city', 'country',
        'brand', 'currency', 'tags'];
      const esc = (v) => {
        if (v === null || v === undefined) return '';
        const s = Array.isArray(v) ? v.join('|') : String(v);
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      // brand is derived ([{id,name}]) — flatten to pipe-joined names for CSV.
      const cell = (r, c) => c === 'brand' ? (r.brands || []).map(b => b.name).join('|') : r[c];
      const csv = [cols.join(','), ...rows.map(r => cols.map(c => esc(cell(r, c))).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Export failed');
    }
  };

  // Inlined column specs — minimal subset of the canonical specs in
  // Suppliers.js / CustomersAdmin.js. Keeps the Contacts page self-contained
  // without dragging in those (page-specific) spec exports. Header aliases
  // cover the most common CIN7 / QB column names for tolerance.
  const SUPPLIER_SPEC = [
    { field: 'supplier_code', label: 'Code', required: true,
      aliases: ['code', 'vendor_code'] },
    { field: 'supplier_name', label: 'Name', required: true,
      aliases: ['name', 'vendor_name', 'company'] },
    { field: 'supplier_type', label: 'Type',
      enum: ['product', 'service', 'raw_material', 'packaging'] },
    { field: 'primary_contact_name', label: 'Contact name', aliases: ['contact'] },
    { field: 'primary_contact_email', label: 'Email', type: 'email', aliases: ['email'] },
    { field: 'primary_contact_phone', label: 'Phone', aliases: ['phone'] },
    { field: 'billing_city', label: 'City' },
    { field: 'billing_country', label: 'Country', aliases: ['country_code'] },
    { field: 'currency', label: 'Currency' },
    { field: 'payment_terms', label: 'Payment terms' },
  ];
  const CUSTOMER_SPEC = [
    { field: 'customer_name', label: 'Name', required: true,
      aliases: ['name', 'display_name', 'company'] },
    { field: 'customer_type', label: 'Type',
      enum: ['dtc', 'retailer', 'distributor'] },
    { field: 'contact_first_name', label: 'First name', aliases: ['first_name'] },
    { field: 'contact_last_name', label: 'Last name', aliases: ['last_name'] },
    { field: 'contact_email', label: 'Email', type: 'email', aliases: ['email'] },
    { field: 'contact_phone', label: 'Phone', aliases: ['phone'] },
    { field: 'delivery_city', label: 'City' },
    { field: 'delivery_country', label: 'Country' },
    { field: 'payment_terms', label: 'Payment terms' },
    { field: 'default_warehouse_code', label: 'Default warehouse' },
  ];
  const activeSpec = importTarget === 'supplier' ? SUPPLIER_SPEC : CUSTOMER_SPEC;
  const activeLabel = importTarget === 'supplier' ? 'supplier' : 'customer';

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        primary="Contacts"
        secondary={[
          { label: `${rows.length} matching` },
          { label: `${totals.supplier || 0} suppliers · ${totals.customer || 0} customers · ${totals.warehouse || 0} warehouses` },
        ]}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
              Add contact
            </Button>
            <EntityActionsMenu
              label="Actions"
              actions={[
                {
                  key: 'import',
                  label: typeFilter
                    ? `Import ${typeFilter === 'supplier' ? 'suppliers' : typeFilter === 'customer' ? 'customers' : 'warehouses'}`
                    : 'Import contacts',
                  hint: typeFilter
                    ? 'CSV or Excel — drag-drop + validation preview'
                    : 'Pick a contact type filter first',
                  icon: <ImportIcon fontSize="small" />,
                  group: 'Data',
                  onClick: () => handleOpenImport('insert'),
                },
                {
                  key: 'export',
                  label: 'Export current view',
                  hint: typeFilter === 'supplier' || typeFilter === 'customer'
                    ? 'Canonical CSV — round-trippable via Update from CSV'
                    : 'Quick CSV of currently visible columns',
                  icon: <ExportIcon fontSize="small" />,
                  group: 'Data',
                  onClick: handleExport,
                },
                {
                  key: 'update-from-csv',
                  label: 'Update from CSV',
                  hint: typeFilter === 'supplier' || typeFilter === 'customer'
                    ? 'Export → edit in Excel → re-upload. Matches rows by id.'
                    : 'Pick Supplier or Customer first',
                  icon: <BulkEditIcon fontSize="small" />,
                  group: 'Data',
                  onClick: () => handleOpenImport('update'),
                },
              ]}
            />
          </Stack>
        }
      />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
            useFlexGap
            flexWrap="wrap"
          >
            <TextField
              size="small"
              placeholder="Search name, code, city, email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: search ? (
                  <IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton>
                ) : null,
              }}
              sx={{ flexGrow: 1, minWidth: 240 }}
            />

            <Stack direction="row" spacing={1}>
              <Chip
                label={`All (${(totals.supplier || 0) + (totals.customer || 0) + (totals.warehouse || 0)})`}
                color={!typeFilter ? 'default' : 'default'}
                variant={!typeFilter ? 'filled' : 'outlined'}
                onClick={() => setTypeFilter('')}
                clickable
              />
              {Object.entries(TYPE_META).map(([t, meta]) => (
                <Chip
                  key={t}
                  label={`${meta.label} (${totals[t] || 0})`}
                  color={typeFilter === t ? meta.color : 'default'}
                  variant={typeFilter === t ? 'filled' : 'outlined'}
                  icon={meta.icon}
                  onClick={() => setTypeFilter(typeFilter === t ? '' : t)}
                  clickable
                />
              ))}
            </Stack>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="country-lbl">Country</InputLabel>
              <Select
                labelId="country-lbl"
                label="Country"
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {countries.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="tag-lbl">Tag</InputLabel>
              <Select
                labelId="tag-lbl"
                label="Tag"
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {allTags.map(t => (
                  <MenuItem key={t.tag} value={t.tag}>{t.tag} ({t.n})</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="brand-lbl">Brand</InputLabel>
              <Select
                labelId="brand-lbl"
                label="Brand"
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {brands.map(b => (
                  <MenuItem key={b.id} value={String(b.id)}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={<Switch
                size="small"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />}
              label="Show inactive"
              sx={{
                m: 0,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                '& .MuiFormControlLabel-label': { fontSize: 13, whiteSpace: 'nowrap' },
              }}
            />
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Sub-type</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell align="right">Open</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={10} align="center"><CircularProgress size={24} /></TableCell></TableRow>
              )}
              {!loading && visibleRows.length === 0 && (
                <TableRow><TableCell colSpan={10} align="center"><Typography variant="body2" color="textSecondary">No contacts match the filters.</Typography></TableCell></TableRow>
              )}
              {!loading && visibleRows.map(row => {
                const meta = TYPE_META[row.entity_type] || {};
                const isInactive = Number(row.is_active) === 0;
                return (
                  <TableRow
                    key={`${row.entity_type}-${row.entity_id}`}
                    hover
                    sx={{ cursor: 'pointer', ...(isInactive ? { opacity: 0.55 } : {}) }}
                    onClick={() => meta.route ? navigate(meta.route(row.entity_id)) : openDrawer(row, 0)}
                  >
                    <TableCell>
                      <Chip
                        label={meta.label || row.entity_type}
                        color={meta.color || 'default'}
                        size="small"
                        icon={meta.icon}
                      />
                    </TableCell>
                    <TableCell>
                      {meta.route ? (
                        <Box
                          component="a"
                          title="Open full overview"
                          onClick={(e) => { e.stopPropagation(); navigate(meta.route(row.entity_id)); }}
                          sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {row.name || '—'}
                        </Box>
                      ) : (
                        <Typography fontWeight="medium" component="span">{row.name || '—'}</Typography>
                      )}
                      {isInactive && (
                        <Chip size="small" label="Inactive" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                        {row.code || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="textSecondary">{row.sub_type || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.contact_email || row.contact_phone ? (
                        <Stack spacing={0.25}>
                          {row.contact_email && <Typography variant="caption">{row.contact_email}</Typography>}
                          {row.contact_phone && <Typography variant="caption" color="textSecondary">{row.contact_phone}</Typography>}
                        </Stack>
                      ) : <Typography variant="caption" color="textSecondary">—</Typography>}
                    </TableCell>
                    <TableCell>{row.city || '—'}</TableCell>
                    <TableCell>{formatCountry(row.country) || '—'}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {(row.brands || []).length ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {row.brands.map(b => (
                            <Chip
                              key={b.id}
                              label={b.name}
                              size="small"
                              variant="outlined"
                              color="secondary"
                              onClick={() => setBrandFilter(String(b.id))}
                              sx={{ height: 22, fontSize: '0.7rem', cursor: 'pointer' }}
                            />
                          ))}
                        </Stack>
                      ) : <Typography variant="caption" color="textSecondary">—</Typography>}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(row.tags || []).map(t => (
                          <Chip
                            key={t}
                            label={t}
                            size="small"
                            variant="outlined"
                            onDelete={() => handleRemoveTag(row, t)}
                            deleteIcon={<CloseIcon fontSize="small" />}
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                        ))}
                        <Tooltip title="Add tag">
                          <IconButton size="small" onClick={() => { setTagDialogRow(row); setNewTagInput(''); }}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => meta.editRoute && navigate(meta.editRoute(row.entity_id))}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Activity timeline">
                        <IconButton
                          size="small"
                          onClick={() => openDrawer(row, 1)}
                        >
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Open canonical detail">
                        <IconButton
                          size="small"
                          onClick={() => meta.route && navigate(meta.route(row.entity_id))}
                        >
                          <OpenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={row.entity_type === 'warehouse'
                        ? 'Deactivate (soft-delete; refuses if any inventory / PO / transfer references it)'
                        : 'Delete (refuses if any transactions reference this entity)'}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => { setDeletingRow(row); setDeletingError(null); }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Card>

      <Dialog open={!!tagDialogRow} onClose={() => setTagDialogRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Add tag to {tagDialogRow?.name}
          <Typography variant="caption" display="block" color="textSecondary">
            {tagDialogRow?.entity_type} · {tagDialogRow?.code}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Tag"
            value={newTagInput}
            onChange={(e) => setNewTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { handleAddTag(); setTagDialogRow(null); } }}
            placeholder="e.g. priority, VIP, paused…"
            inputProps={{ maxLength: 64 }}
            sx={{ mt: 1 }}
          />
          {allTags.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="textSecondary">Existing tags (click to reuse):</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {allTags.map(t => (
                  <Chip
                    key={t.tag}
                    label={t.tag}
                    size="small"
                    variant="outlined"
                    onClick={() => setNewTagInput(t.tag)}
                    clickable
                  />
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagDialogRow(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => { await handleAddTag(); setTagDialogRow(null); }}
            disabled={!newTagInput.trim()}
            startIcon={<TagIcon />}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>What kind of contact?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Pick a type — you'll land on the canonical entry form. The new record then appears in this list automatically.
          </Typography>
          <Stack spacing={1.5}>
            {Object.entries(TYPE_META).map(([t, meta]) => (
              <Card
                key={t}
                variant="outlined"
                sx={{ cursor: 'pointer', '&:hover': { borderColor: `${meta.color}.main`, boxShadow: 1 } }}
                onClick={() => { setAddOpen(false); navigate(meta.newRoute()); }}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 1.5 }}>
                  <Chip
                    label={meta.label}
                    color={meta.color}
                    icon={meta.icon}
                    size="small"
                    sx={{ flexShrink: 0 }}
                  />
                  <Box>
                    <Typography variant="subtitle2">New {meta.label.toLowerCase()}</Typography>
                    <Typography variant="caption" color="textSecondary">{meta.blurb}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={!!drawerRow}
        onClose={() => setDrawerRow(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
      >
        {drawerRow && (() => {
          const meta = TYPE_META[drawerRow.entity_type] || {};
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Chip label={meta.label || drawerRow.entity_type} color={meta.color} icon={meta.icon} size="small" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6">{drawerRow.name}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {drawerRow.code} {drawerRow.sub_type ? `· ${drawerRow.sub_type}` : ''}
                  </Typography>
                </Box>
                <IconButton onClick={() => setDrawerRow(null)} size="small"><CloseIcon /></IconButton>
              </Box>
              <Divider />

              <Tabs
                value={drawerTab}
                onChange={(_, v) => {
                  setDrawerTab(v);
                  if (v === 1 && timelineEvents.length === 0) loadTimeline(drawerRow);
                }}
              >
                <Tab label="Overview" />
                <Tab label="Activity" />
              </Tabs>
              <Divider />

              <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                {drawerTab === 0 && (
                  <Stack spacing={1.5}>
                    {drawerRow.contact_name && (
                      <Box><Typography variant="caption" color="textSecondary">Contact</Typography>
                        <Typography variant="body2">{drawerRow.contact_name}</Typography></Box>
                    )}
                    {drawerRow.contact_email && (
                      <Box><Typography variant="caption" color="textSecondary">Email</Typography>
                        <Typography variant="body2"><a href={`mailto:${drawerRow.contact_email}`}>{drawerRow.contact_email}</a></Typography></Box>
                    )}
                    {drawerRow.contact_phone && (
                      <Box><Typography variant="caption" color="textSecondary">Phone</Typography>
                        <Typography variant="body2">{drawerRow.contact_phone}</Typography></Box>
                    )}
                    {(drawerRow.city || drawerRow.country) && (
                      <Box><Typography variant="caption" color="textSecondary">Location</Typography>
                        <Typography variant="body2">
                          {[drawerRow.city, formatCountry(drawerRow.country)].filter(Boolean).join(', ') || '—'}
                        </Typography></Box>
                    )}
                    {drawerRow.currency && (
                      <Box><Typography variant="caption" color="textSecondary">Currency</Typography>
                        <Typography variant="body2">{drawerRow.currency}</Typography></Box>
                    )}
                    <Box>
                      <Typography variant="caption" color="textSecondary">Tags</Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                        {(drawerRow.tags || []).length === 0 && (
                          <Typography variant="body2" color="textSecondary">No tags</Typography>
                        )}
                        {(drawerRow.tags || []).map(t => (
                          <Chip key={t} label={t} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </Box>
                    <Box sx={{ pt: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<OpenIcon />}
                        onClick={() => meta.route && navigate(meta.route(drawerRow.entity_id))}
                      >
                        Open canonical detail
                      </Button>
                    </Box>
                  </Stack>
                )}

                {drawerTab === 1 && (
                  <>
                    {timelineLoading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>}
                    {/* Customers: order history IS the activity (audit log is empty for them). */}
                    {!timelineLoading && drawerRow.entity_type === 'customer' && (
                      drawerOrders.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">No orders yet for this customer.</Typography>
                      ) : (
                        <List dense disablePadding>
                          {drawerOrders.map(o => (
                            <ListItem key={`${o.brand_id}-${o.order_id}`} alignItems="flex-start" sx={{ borderLeft: '2px solid', borderColor: 'primary.light', mb: 1, pl: 1.5 }}>
                              <ListItemText
                                primary={
                                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Box
                                      component="a"
                                      onClick={(e) => { e.stopPropagation(); navigate(`/wholesale/new?edit=${encodeURIComponent(o.order_id)}&brand=${o.brand_id}`); }}
                                      sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                    >
                                      {(o.order_id || '').replace(/^CIN7-/, '')}
                                    </Box>
                                    <Chip label={o.fulfillment_status || '—'} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                    {o.qb_invoice_id ? <Chip label={`inv ${o.qb_invoice_id}`} size="small" color="info" sx={{ height: 18, fontSize: '0.65rem' }} /> : null}
                                  </Stack>
                                }
                                secondary={`${o.event_date || ''}${o.shipped_date ? ' · shipped ' + o.shipped_date : ''} · ${o.currency || ''} ${Number(o.grand_total ?? o.order_revenue ?? 0).toFixed(2)}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )
                    )}
                    {/* Suppliers: purchase orders ARE the activity. */}
                    {!timelineLoading && drawerRow.entity_type === 'supplier' && (
                      drawerOrders.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">No purchase orders for this supplier yet.</Typography>
                      ) : (
                        <List dense disablePadding>
                          {drawerOrders.map(po => (
                            <ListItem key={po.id} alignItems="flex-start" sx={{ borderLeft: '2px solid', borderColor: 'primary.light', mb: 1, pl: 1.5 }}>
                              <ListItemText
                                primary={
                                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Box
                                      component="a"
                                      onClick={(e) => { e.stopPropagation(); navigate(`/procurement/purchase-orders/${po.id}`); }}
                                      sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                    >
                                      {po.po_number || po.id}
                                    </Box>
                                    {po.status ? <Chip label={po.status} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} /> : null}
                                  </Stack>
                                }
                                secondary={`${po.po_date || ''}${po.expected_delivery_date ? ' · exp ' + po.expected_delivery_date : ''}${po.total_amount != null ? ' · ' + (po.currency || '') + ' ' + Number(po.total_amount).toFixed(2) : ''}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )
                    )}
                    {!timelineLoading && drawerRow.entity_type !== 'customer' && drawerRow.entity_type !== 'supplier' && timelineEvents.length === 0 && (
                      <Typography variant="body2" color="textSecondary">No activity recorded yet for this contact.</Typography>
                    )}
                    {!timelineLoading && drawerRow.entity_type !== 'customer' && drawerRow.entity_type !== 'supplier' && timelineEvents.length > 0 && (
                      <List dense disablePadding>
                        {timelineEvents.map(ev => {
                          let changes = {};
                          try { changes = typeof ev.changes === 'string' ? JSON.parse(ev.changes) : (ev.changes || {}); } catch { changes = {}; }
                          const status = changes.status ?? '—';
                          const path = changes.path || '';
                          return (
                            <ListItem key={ev.id} alignItems="flex-start" sx={{ borderLeft: '2px solid', borderColor: ev.source === 'tag' ? 'secondary.light' : 'primary.light', mb: 1, pl: 1.5 }}>
                              <ListItemText
                                primary={
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2" fontWeight="medium">{ev.action || '(no action)'}</Typography>
                                    {ev.source === 'tag' && <Chip label="tag" size="small" color="secondary" sx={{ height: 18, fontSize: '0.65rem' }} />}
                                    <Chip label={`HTTP ${status}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                  </Stack>
                                }
                                secondary={
                                  <>
                                    <Typography variant="caption" color="textSecondary" component="span">
                                      {new Date(ev.ts).toLocaleString()} · {ev.user_email || `user ${ev.user_id}`}
                                    </Typography>
                                    {path && <Typography variant="caption" color="textSecondary" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>{path}</Typography>}
                                  </>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </>
                )}
              </Box>
            </Box>
          );
        })()}
      </Drawer>

      {/* Delete confirmation — routes to the right backend endpoint by
          entity_type. Each endpoint enforces its own reference guard; we
          surface the 409 structured response inline (entity counts +
          inventory units) so the operator knows what's blocking them. */}
      <BulkImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        entityLabel={activeLabel}
        mode={importMode}
        columnSpec={activeSpec}
        sampleRows={[]}
        onSubmit={handleBulkImport}
        helpText={
          importMode === 'update'
            ? `Round-trip workflow for ${activeLabel}s: keep the id column, edit any other cells, leave blank to skip. __CLEAR__ explicitly empties a cell.`
            : `Importing ${activeLabel}s — header row required; column order doesn't matter; headers matched case-insensitively. For more columns (full address, banking, terms HTML) use the dedicated ${activeLabel} page.`
        }
      />

      <Dialog open={Boolean(deletingRow)} onClose={() => !deletingInProgress && setDeletingRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {deletingRow?.entity_type === 'warehouse' ? 'Deactivate warehouse' :
           deletingRow?.entity_type === 'supplier' ? 'Delete supplier' :
           deletingRow?.entity_type === 'customer' ? 'Delete customer' : 'Delete'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {deletingRow?.entity_type === 'warehouse' ? 'Deactivate' : 'Delete'} <strong>{deletingRow?.name}</strong>
            {deletingRow?.code ? <> ({deletingRow.code})</> : null}?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {deletingRow?.entity_type === 'warehouse'
              ? 'Soft-delete — inventory rows + history stay intact.'
              : 'Hard-delete. Refused if any transactions / POs / orders reference this record.'}
          </Typography>
          {deletingError && (
            <Alert severity="error" sx={{ mt: 2 }} icon={false}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                Delete disabled
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>{deletingError.message}</Typography>
              {Array.isArray(deletingError.references) && deletingError.references.length > 0 && (
                <Box component="ul" sx={{ pl: 2, m: 0, mt: 1, fontSize: 12 }}>
                  {deletingError.references.map((r) => (
                    <li key={r.entity}>
                      <Typography variant="caption">
                        <strong>{r.entity}:</strong> {Number(r.n).toLocaleString()}
                        {r.entity === 'inventory_batches' && Number(r.qty) > 0 && (
                          <> ({Number(r.qty).toLocaleString()} units)</>
                        )}
                      </Typography>
                      {Array.isArray(r.samples) && r.samples.length > 0 && (
                        <Box sx={{ mt: 0.25, ml: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {Number(r.n) > r.samples.length
                              ? `First ${r.samples.length}: `
                              : `${r.samples.length === 1 ? '' : 'All: '}`}
                            {r.samples.map((s, i) => (
                              <React.Fragment key={`${r.entity}-${s.id ?? i}`}>
                                {i > 0 && ', '}
                                {s.route ? (
                                  <RouterLink to={s.route} style={{ fontWeight: 500 }}>
                                    {s.label || s.id}
                                  </RouterLink>
                                ) : (
                                  <span>{s.label || s.id}</span>
                                )}
                              </React.Fragment>
                            ))}
                            {Number(r.n) > r.samples.length && ` … +${(Number(r.n) - r.samples.length).toLocaleString()} more`}
                          </Typography>
                        </Box>
                      )}
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingRow(null)} disabled={deletingInProgress}>Cancel</Button>
          <Button color="error" variant="contained" onClick={performDelete} disabled={deletingInProgress}>
            {deletingInProgress
              ? 'Working…'
              : deletingRow?.entity_type === 'warehouse' ? 'Deactivate' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
